import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { getToken } from '~/lib/auth'
import { API_URL, IMPERSONATE, IS_LOCAL } from '~/lib/constants'
import { updateServerOffset } from '~/lib/serverClock'
import { pushError, setDialog } from '~/store/ui'
import { selectIsAdmin } from '../auth/selectors'

const logger = console.scope('api', true)

const ENDPOINT_LABELS = {
	startSession: 'Start Session',
	stopSession: 'Stop Session',
	getProject: 'Load Project',
	addProject: 'Create Project',
	updateProject: 'Update Project',
	deleteProject: 'Delete Project',
	deleteProjects: 'Delete Projects',
	addPreset: 'Create Preset',
	addPresets: 'Create Presets',
	updatePreset: 'Update Preset',
	deletePresets: 'Delete Presets',
	startRecording: 'Start Recording',
	stopRecording: 'Stop Recording',
	deleteRecording: 'Delete Recording',
	deleteRecordings: 'Delete Recordings',
	addAsset: 'Upload Asset',
	updateAsset: 'Update Asset',
	deleteAssets: 'Delete Assets',
	saveTemplateAs: 'Save Template',
	addTemplate: 'Create Template',
	deleteTemplate: 'Delete Template',
	addMap: 'Create Map',
	updateMap: 'Update Map',
	deleteMap: 'Delete Map',
}

const rawBaseQuery = fetchBaseQuery({
	baseUrl: API_URL,
	credentials: IS_LOCAL ? 'include' : 'same-origin',
	prepareHeaders: async (headers, { getState }) => {
		const s = getState()

		let token
		try {
			token = await getToken()
		} catch {
			token = s.auth.token
		}

		if (token) {
			headers.set('authorization', `Bearer ${token}`)
			const isAdmin = selectIsAdmin(s)
			if (isAdmin && IMPERSONATE) {
				headers.set('X-Impersonate-User', IMPERSONATE)
			}
		}
		return headers
	},
})

const SUPPRESSED_ERRORS = [{ endpoint: 'deleteAssets', status: 409 }]

const baseQueryWithErrorHandling = async (args, api, extraOptions) => {
	const result = await rawBaseQuery(args, api, extraOptions)

	// Keep the server/local clock offset fresh from every response's `Date` header, so timestamp-vs-now math (session progress) is immune to a skewed local clock. See ~/lib/serverClock.
	updateServerOffset(result.meta?.response?.headers?.get('date'))

	if (result.error) {
		const { status, data } = result.error
		const endpointName = api.endpoint

		const isMutation = api.type === 'mutation'
		const isCriticalQuery = ['getActiveSession', 'getProject'].includes(endpointName)

		if (isMutation || isCriticalQuery) {
			const suppressed = SUPPRESSED_ERRORS.find(h => h.endpoint === endpointName && h.status === status)
			if (suppressed) return result

			const operationName = ENDPOINT_LABELS[endpointName] || endpointName
			const serverMessage = data?.message || data?.error || ''
			const statusText = typeof status === 'number' ? `HTTP ${status}` : status

			const requestId = result.meta?.response?.headers?.get('x-request-id')
			logger.error(`API Error [${endpointName}]`, { status, data, requestId })

			api.dispatch(
				pushError({
					code: `API_${endpointName.toUpperCase()}_FAILED`,
					title: `${operationName} Failed`,
					message: serverMessage
						? `${operationName} failed: ${serverMessage}\n\nError code: ${statusText}`
						: `${operationName} failed.\n\nError code: ${statusText}`,
				})
			)

			api.dispatch(
				setDialog({
					title: `${operationName} Failed`,
					message: serverMessage
						? `${operationName} failed: ${serverMessage}\n\nError code: ${statusText}`
						: `${operationName} failed.\n\nError code: ${statusText}`,
					okayLabel: 'OK',
					isError: true,
				})
			)
		}
	}

	return result
}

const ASSET = 'Asset'
const CREDITS = 'Credits'
const PRESET = 'Preset'
const PROJECT = 'Project'
const RECORDING = 'Recording'
const SESSION = 'Session'
const TEMPLATE = 'Template'
const MAP = 'Map'

const LIST = 'LIST'
export const GET_ACTIVE_SESSION = { type: SESSION, id: 'active' }
export const LIST_ASSETS = { type: ASSET, id: LIST }
export const LIST_PROJECTS = { type: PROJECT, id: LIST }
export const LIST_RECORDINGS = { type: RECORDING, id: LIST }
export const LIST_SESSIONS = { type: SESSION, id: LIST }
export const LIST_TEMPLATES = { type: TEMPLATE, id: LIST }
export const LIST_MAPS = { type: MAP, id: LIST }

export const TAGS = {
	ASSET,
	CREDITS,
	PRESET,
	PROJECT,
	RECORDING,
	SESSION,
	LIST_PROJECTS,
	LIST_RECORDINGS,
	LIST_SESSIONS,
	LIST_TEMPLATES,
	LIST_ASSETS,
	LIST_MAPS,
}

export { ASSET, CREDITS, LIST, PRESET, PROJECT, RECORDING, SESSION, TEMPLATE, MAP }

export const templateEndpointName = 'projectTemplates'

// Lives in transforms.js with `transformProject`, which is its only structural caller; re-exported here because importing it from the api module is what every existing caller does.
export { conformPresetSettings } from './transforms'

const api = createApi({
	reducerPath: 'api',
	baseQuery: baseQueryWithErrorHandling,
	tagTypes: [SESSION, LIST_SESSIONS, PROJECT, LIST_PROJECTS, PRESET, CREDITS, RECORDING, LIST_RECORDINGS],
	endpoints: () => ({}),
})

export default api
